/**
 * @file RayCastingTool.tsx
 * @description Interactive ray casting tool for spatial validation and ADA compliance checking
 */

import { toast } from '@/components/common/Toast';
import type { MultiRayCastResult } from '@/services/core/geometryTools';
import { GeometryTools } from '@/services/core/geometryTools';
import { PropertiesService } from '@/services/core/propertiesTools';
import { ViewerService } from '@/services/core/viewerTools';
import { useViewerStore } from '@/store/viewerStore';
import { AlertCircle, ChevronDown, ChevronRight, Download, Eye, EyeOff, Play, Target, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';

// THREE is available globally when Forge Viewer is loaded
declare const THREE: any;
declare const Autodesk: any;

interface DetectedElement {
  dbId: number;
  name: string;
  hitCount: number;
  directions: string[];
  distances: number[];
  averageDistance: number;
}

interface RayCastingResult {
  sourceElement: {
    dbId: number;
    name: string;
    boundingBox: any;
    center: any;
    orientation: any;
  };
  rays: MultiRayCastResult[];
  detectedElements: DetectedElement[];
  summary: {
    totalRays: number;
    hitsCount: number;
    missesCount: number;
    uniqueElements: number;
    filteredElements: number;
  };
  timestamp: Date;
}

interface ElementNameCache {
  [dbId: number]: string;
}

export function RayCastingTool() {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [sourceDbId, setSourceDbId] = useState('');
  const [maxDistance, setMaxDistance] = useState(10);
  const [heights] = useState([0.5, 1.5, 3.0, 5.0]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<RayCastingResult | null>(null);
  const [showRays, setShowRays] = useState(true);
  const [elementNames, setElementNames] = useState<ElementNameCache>({});
  const [highlightedElements, setHighlightedElements] = useState<Set<number>>(new Set());
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 });
  
  // Refs for debouncing and virtual scrolling
  const sourceDbIdRef = useRef(sourceDbId);
  const maxDistanceRef = useRef(maxDistance);
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { viewer, isModelLoaded } = useViewerStore();

  const overlaySceneName = 'ray-casting-scene';

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (viewer?.impl) {
        clearVisualization();
      }
    };
  }, [viewer]);

  // Clear visualization when model changes
  useEffect(() => {
    if (viewer && isModelLoaded) {
      clearVisualization();
      setResults(null);
      setElementNames({});
      setHighlightedElements(new Set());
    }
  }, [isModelLoaded]);

  // Convert element ID to DbId
  const resolveElementId = async (elementId: string): Promise<number | null> => {
    if (!elementId.trim()) return null;

    const id = elementId.trim();
    const numId = parseInt(id);

    if (!isNaN(numId)) {
      return numId;
    }

    // Try as Revit Element ID
    const dbId = await PropertiesService.findDbIdByRevitId(viewer, id);
    return dbId;
  };

  // Batch fetch element names for better performance
  const getElementNamesBatch = useCallback(async (dbIds: number[]): Promise<Map<number, string>> => {
    const results = new Map<number, string>();
    const uncachedIds: number[] = [];
    
    // Check cache first
    for (const dbId of dbIds) {
      if (elementNames[dbId]) {
        results.set(dbId, elementNames[dbId]);
      } else {
        uncachedIds.push(dbId);
      }
    }
    
    // Batch fetch uncached names
    if (uncachedIds.length > 0) {
      const fetchPromises = uncachedIds.map(async (dbId) => {
        try {
          const name = await GeometryTools.getElementName(viewer, dbId);
          return { dbId, name };
        } catch {
          return { dbId, name: `Element_${dbId}` };
        }
      });
      
      // Process in chunks to avoid overwhelming the system
      const chunkSize = 10;
      const chunks = [];
      for (let i = 0; i < fetchPromises.length; i += chunkSize) {
        chunks.push(fetchPromises.slice(i, i + chunkSize));
      }
      
      const newNames: { [key: number]: string } = {};
      for (const chunk of chunks) {
        const chunkResults = await Promise.all(chunk);
        for (const { dbId, name } of chunkResults) {
          results.set(dbId, name);
          newNames[dbId] = name;
        }
      }
      
      // Update cache
      setElementNames(prev => ({ ...prev, ...newNames }));
    }
    
    return results;
  }, [viewer, elementNames]);
  
  // Get single element name (uses batch internally for consistency)
  const getElementName = useCallback(async (dbId: number): Promise<string> => {
    const results = await getElementNamesBatch([dbId]);
    return results.get(dbId) || `Element_${dbId}`;
  }, [getElementNamesBatch]);

  // Clear ray visualization
  const clearVisualization = useCallback(() => {
    if (!viewer?.impl) return;

    try {
      // Clear overlay scene
      if (viewer.impl.overlayScenes[overlaySceneName]) {
        viewer.impl.clearOverlay(overlaySceneName);
        viewer.impl.removeOverlayScene(overlaySceneName);
      }

      // Clear highlights
      viewer.clearThemingColors();
      
      viewer.impl.invalidate(true, true, true);
    } catch (error) {
    }
  }, [viewer]);

  // Optimized ray visualization using instanced geometry
  const visualizeRays = useCallback((rays: MultiRayCastResult[]) => {
    if (!viewer?.impl || !showRays) return;

    try {
      // Create overlay scene if needed
      if (!viewer.impl.overlayScenes[overlaySceneName]) {
        viewer.impl.createOverlayScene(overlaySceneName);
      } else {
        viewer.impl.clearOverlay(overlaySceneName);
      }

      // Group rays by hit/miss for batch rendering
      const hitRays: MultiRayCastResult[] = [];
      const missRays: MultiRayCastResult[] = [];
      
      rays.forEach(ray => {
        if (ray.hasHit) {
          hitRays.push(ray);
        } else {
          missRays.push(ray);
        }
      });
      
      // Create merged geometry for better performance
      const createMergedLines = (rayGroup: MultiRayCastResult[], color: number) => {
        if (rayGroup.length === 0) return;
        
        const positions: number[] = [];
        const colors: number[] = [];
        const r = ((color >> 16) & 255) / 255;
        const g = ((color >> 8) & 255) / 255;
        const b = (color & 255) / 255;
        
        rayGroup.forEach(ray => {
          // Calculate end point
          let endPoint: any;
          if (ray.hasHit && ray.hits.length > 0) {
            endPoint = ray.hits[0].point;
          } else {
            const direction = new THREE.Vector3(ray.direction.x, ray.direction.y, ray.direction.z);
            endPoint = new THREE.Vector3(ray.origin.x, ray.origin.y, ray.origin.z).add(
              direction.multiplyScalar(ray.maxDistance)
            );
          }
          
          // Add line segment
          positions.push(ray.origin.x, ray.origin.y, ray.origin.z);
          positions.push(endPoint.x, endPoint.y, endPoint.z);
          colors.push(r, g, b, r, g, b);
        });
        
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        
        const material = new THREE.LineBasicMaterial({
          vertexColors: true,
          linewidth: 2,
          opacity: 0.8,
          transparent: true
        });
        
        const lines = new THREE.LineSegments(geometry, material);
        viewer.impl.addOverlay(overlaySceneName, lines);
        
        // Add arrow heads (simplified - only at origins)
        const arrowGeometry = new THREE.ConeGeometry(0.2, 0.5, 4);
        const arrowMaterial = new THREE.MeshBasicMaterial({ color, opacity: 0.8, transparent: true });
        
        rayGroup.forEach(ray => {
          const arrow = new THREE.Mesh(arrowGeometry, arrowMaterial);
          const dir = new THREE.Vector3(ray.direction.x, ray.direction.y, ray.direction.z).normalize();
          arrow.position.copy(ray.origin);
          arrow.position.add(dir.multiplyScalar(0.5));
          arrow.lookAt(new THREE.Vector3(ray.origin.x, ray.origin.y, ray.origin.z).add(dir));
          viewer.impl.addOverlay(overlaySceneName, arrow);
        });
      };
      
      // Render hit and miss rays in batches
      createMergedLines(hitRays, 0x00ff00); // Green for hits
      createMergedLines(missRays, 0xff0000); // Red for misses

      viewer.impl.invalidate(true, true, true);
    } catch (error) {
    }
  }, [viewer, showRays]);

  // Memoized highlighting logic
  const toggleElementHighlight = useCallback((dbId: number) => {
    if (!viewer) return;

    setHighlightedElements(prevHighlighted => {
      const newHighlighted = new Set(prevHighlighted);
      
      if (newHighlighted.has(dbId)) {
        newHighlighted.delete(dbId);
      } else {
        newHighlighted.add(dbId);
      }
      
      // Apply visual changes
      requestAnimationFrame(() => {
        if (newHighlighted.size > 0) {
          viewer.clearThemingColors();
          viewer.isolate(Array.from(newHighlighted));
          newHighlighted.forEach(id => {
            viewer.setThemingColor(id, new THREE.Vector4(1, 0, 0, 0.6));
          });
          viewer.fitToView(Array.from(newHighlighted), viewer.model, true);
        } else {
          viewer.clearThemingColors();
          viewer.showAll();
          viewer.fitToView();
        }
        viewer.impl.invalidate(true, true, true);
      });
      
      return newHighlighted;
    });
  }, [viewer]);

  // Memoized toggle all with optimized updates
  const toggleAllElementsHighlight = useCallback(() => {
    if (!viewer || !results) return;

    const allDbIds = results.detectedElements.map(elem => elem.dbId);
    
    setHighlightedElements(prevHighlighted => {
      // Check if all are already highlighted
      const allHighlighted = allDbIds.every(id => prevHighlighted.has(id));
      const newHighlighted = new Set(prevHighlighted);
      
      if (allHighlighted) {
        // Remove all highlights
        allDbIds.forEach(id => newHighlighted.delete(id));
      } else {
        // Add all highlights
        allDbIds.forEach(id => newHighlighted.add(id));
      }
      
      // Apply visual changes
      requestAnimationFrame(() => {
        if (allHighlighted) {
          viewer.clearThemingColors();
          viewer.showAll();
          viewer.fitToView();
        } else {
          viewer.clearThemingColors();
          viewer.isolate(allDbIds);
          allDbIds.forEach(dbId => {
            viewer.setThemingColor(dbId, new THREE.Vector4(1, 0, 0, 0.6));
          });
          viewer.fitToView(allDbIds, viewer.model, true);
        }
        viewer.impl.invalidate(true, true, true);
      });
      
      return newHighlighted;
    });
  }, [viewer, results]);

  // Run ray casting
  const handleRunRayCasting = async () => {
    if (!viewer || !isModelLoaded) {
      toast.error('Model not loaded');
      return;
    }

    if (!sourceDbId.trim()) {
      toast.error('Please enter a source element ID');
      return;
    }

    setIsProcessing(true);
    clearVisualization();

    try {
      // Resolve element ID
      const dbId = await resolveElementId(sourceDbId);
      if (!dbId) {
        toast.error('Invalid element ID');
        setIsProcessing(false);
        return;
      }

      // Get element info
      const boundingBox = GeometryTools.getElementBoundingBox(viewer, dbId);
      if (!boundingBox) {
        toast.error('Element not found or has no geometry');
        setIsProcessing(false);
        return;
      }

      const elementName = await getElementName(dbId);
      const center = new THREE.Vector3();
      boundingBox.getCenter(center);
      const orientation = await GeometryTools.getElementOrientation(viewer, dbId);

      // Cast rays
      const rays = await GeometryTools.castMultipleRays(viewer, dbId, {
        maxDistance: maxDistance,
        heights: heights
      });

      // Reduced logging for better performance

      // Process results and group by detected elements
      const elementMap = new Map<number, {
        dbId: number;
        name: string;
        hitCount: number;
        directions: Set<string>;
        distances: number[];
      }>();
      
      let hitsCount = 0;
      let missesCount = 0;

      // Collect element data from all rays
      rays.forEach(ray => {
        if (ray.hasHit) {
          hitsCount++;
          ray.hits.forEach(hit => {
            if (!elementMap.has(hit.dbId)) {
              elementMap.set(hit.dbId, {
                dbId: hit.dbId,
                name: '',
                hitCount: 0,
                directions: new Set(),
                distances: []
              });
            }
            
            const element = elementMap.get(hit.dbId)!;
            element.hitCount++;
            element.directions.add(ray.directionName);
            element.distances.push(hit.distance);
          });
        } else {
          missesCount++;
        }
      });

      // Batch fetch all names for better performance
      const dbIds = Array.from(elementMap.keys());
      const nameMap = await getElementNamesBatch(dbIds);
      
      // Filter and create detected elements
      const detectedElements: DetectedElement[] = [];
      for (const [dbId, elementData] of elementMap) {
        const name = nameMap.get(dbId) || `Element_${dbId}`;
        
        // Filter: only include elements with square brackets in the name
        if (name.includes('[') && name.includes(']')) {
          detectedElements.push({
            dbId: elementData.dbId,
            name: name,
            hitCount: elementData.hitCount,
            directions: Array.from(elementData.directions),
            distances: elementData.distances,
            averageDistance: elementData.distances.reduce((a, b) => a + b, 0) / elementData.distances.length
          });
        }
      }
      
      // Sort by hit count (descending)
      detectedElements.sort((a, b) => b.hitCount - a.hitCount);

      // Create result object
      const result: RayCastingResult = {
        sourceElement: {
          dbId: dbId,
          name: elementName,
          boundingBox: boundingBox,
          center: center,
          orientation: orientation
        },
        rays: rays,
        detectedElements: detectedElements,
        summary: {
          totalRays: rays.length,
          hitsCount: hitsCount,
          missesCount: missesCount,
          uniqueElements: elementMap.size,
          filteredElements: detectedElements.length
        },
        timestamp: new Date()
      };

      setResults(result);
      setVisibleRange({ start: 0, end: 20 }); // Reset virtual scroll
      visualizeRays(rays);

      toast.success(`Ray casting complete: ${hitsCount} hits, ${missesCount} misses`);
    } catch (error) {
      toast.error('Ray casting failed');
    } finally {
      setIsProcessing(false);
    }
  };

  // Clear results
  const handleClear = () => {
    clearVisualization();
    setResults(null);
    setHighlightedElements(new Set());
    setVisibleRange({ start: 0, end: 20 });
    toast.info('Results cleared');
  };

  // Toggle ray visibility
  const handleToggleRays = () => {
    setShowRays(!showRays);
    if (results) {
      if (!showRays) {
        visualizeRays(results.rays);
      } else {
        clearVisualization();
      }
    }
  };

  // Export results to JSON
  const handleExportResults = () => {
    if (!results) return;

    const exportData = {
      timestamp: results.timestamp,
      source: {
        dbId: results.sourceElement.dbId,
        name: results.sourceElement.name,
        center: {
          x: results.sourceElement.center.x,
          y: results.sourceElement.center.y,
          z: results.sourceElement.center.z
        },
        orientation: {
          forward: {
            x: results.sourceElement.orientation.forward.x,
            y: results.sourceElement.orientation.forward.y,
            z: results.sourceElement.orientation.forward.z
          },
          confidence: results.sourceElement.orientation.confidence
        }
      },
      configuration: {
        maxDistance: maxDistance,
        heights: heights,
        directions: ['forward', 'backward', 'right', 'left']
      },
      summary: {
        totalRays: results.summary.totalRays,
        hitsCount: results.summary.hitsCount,
        missesCount: results.summary.missesCount,
        totalElementsDetected: results.summary.uniqueElements,
        filteredElements: results.summary.filteredElements
      },
      detectedElements: results.detectedElements.map(elem => ({
        dbId: elem.dbId,
        name: elem.name,
        rayHitCount: elem.hitCount,
        directions: elem.directions,
        averageDistance: elem.averageDistance.toFixed(2)
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ray-casting-results-${results.sourceElement.dbId}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Results exported to JSON');
  };

  return (
    <div className="bg-gray-800 rounded-lg shadow-sm text-gray-100">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Target className="w-5 h-5 text-blue-500" />
          <span className="font-medium text-gray-100">Ray Casting Tool</span>
        </div>
        {isCollapsed ? <ChevronRight className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>

      {!isCollapsed && (
        <div className="p-4 border-t border-gray-700 space-y-4">
          {/* Input Section */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Source Element ID
              </label>
              <input
                type="text"
                value={sourceDbId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSourceDbId(value);
                  sourceDbIdRef.current = value;
                }}
                placeholder="Enter DbId or Revit Element ID"
                className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-700 text-gray-100"
                disabled={isProcessing}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Max Distance: {maxDistance} feet
              </label>
              <input
                type="range"
                min="1"
                max="50"
                value={maxDistance}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setMaxDistance(value);
                  maxDistanceRef.current = value;
                  
                  // Debounce updates
                  if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                  }
                  debounceTimerRef.current = setTimeout(() => {
                    // Trigger any necessary updates here if needed
                  }, 150);
                }}
                className="w-full"
                disabled={isProcessing}
              />
            </div>

            <div className="text-sm text-gray-400">
              Heights: {heights.join(', ')} feet
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={handleRunRayCasting}
              disabled={isProcessing || !isModelLoaded}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Play className="w-4 h-4" />
              {isProcessing ? 'Processing...' : 'Run Ray Casting'}
            </button>

            <button
              onClick={handleClear}
              disabled={isProcessing || !results}
              className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Clear
            </button>

            <button
              onClick={handleToggleRays}
              disabled={!results}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {showRays ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showRays ? 'Hide Rays' : 'Show Rays'}
            </button>

            <button
              onClick={handleExportResults}
              disabled={!results}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-4 h-4" />
              Export JSON
            </button>
          </div>

          {/* Results Display */}
          {results && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-gray-900 p-3 rounded-md">
                <h4 className="font-medium text-gray-100 mb-2">Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-400">Source:</span>
                    <span className="ml-2 font-medium">{results.sourceElement.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Total Rays:</span>
                    <span className="ml-2 font-medium">{results.summary.totalRays}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Hits:</span>
                    <span className="ml-2 font-medium text-green-600">{results.summary.hitsCount}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Misses:</span>
                    <span className="ml-2 font-medium text-red-600">{results.summary.missesCount}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Elements Detected:</span>
                    <span className="ml-2 font-medium">{results.summary.uniqueElements}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Filtered (with []):</span>
                    <span className="ml-2 font-medium text-blue-400">{results.summary.filteredElements}</span>
                  </div>
                </div>
              </div>

              {/* Detected Elements Table */}
              <div className="bg-gray-900 p-3 rounded-md">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-100">Detected Elements (Filtered)</h4>
                  {results.detectedElements.length > 0 && (
                    <button
                      onClick={toggleAllElementsHighlight}
                      className={`px-3 py-1 rounded text-sm ${
                        results.detectedElements.every(elem => highlightedElements.has(elem.dbId))
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                    >
                      {results.detectedElements.every(elem => highlightedElements.has(elem.dbId)) ? 'Hide All' : 'Show All'}
                    </button>
                  )}
                </div>
                {results.detectedElements.length > 0 ? (
                  <div 
                    ref={tableContainerRef}
                    className="max-h-96 overflow-y-auto"
                    onScroll={(e) => {
                      const container = e.currentTarget;
                      const scrollTop = container.scrollTop;
                      const itemHeight = 32; // Approximate row height
                      const containerHeight = container.clientHeight;
                      const visibleItems = Math.ceil(containerHeight / itemHeight);
                      const bufferItems = 5;
                      
                      const newStart = Math.max(0, Math.floor(scrollTop / itemHeight) - bufferItems);
                      const newEnd = Math.min(
                        results.detectedElements.length,
                        newStart + visibleItems + (bufferItems * 2)
                      );
                      
                      if (newStart !== visibleRange.start || newEnd !== visibleRange.end) {
                        setVisibleRange({ start: newStart, end: newEnd });
                      }
                    }}
                  >
                    <table className="w-full text-sm">
                      <thead className="bg-gray-800 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left text-gray-300">Element Name</th>
                          <th className="px-2 py-1 text-left text-gray-300">DbId</th>
                          <th className="px-2 py-1 text-left text-gray-300">Ray Hits</th>
                          <th className="px-2 py-1 text-left text-gray-300">Directions</th>
                          <th className="px-2 py-1 text-left text-gray-300">Avg Distance</th>
                          <th className="px-2 py-1 text-center text-gray-300">Show</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {/* Virtual spacer for items before visible range */}
                        {visibleRange.start > 0 && (
                          <tr style={{ height: `${visibleRange.start * 32}px` }}>
                            <td colSpan={6}></td>
                          </tr>
                        )}
                        
                        {/* Visible items */}
                        {results.detectedElements.slice(visibleRange.start, visibleRange.end).map(elem => (
                          <tr key={elem.dbId} className="hover:bg-gray-700" style={{ height: '32px' }}>
                            <td className="px-2 py-1 text-gray-300">
                              <span className="font-medium">{elem.name}</span>
                            </td>
                            <td className="px-2 py-1 text-gray-300">{elem.dbId}</td>
                            <td className="px-2 py-1 text-center">
                              <span className="bg-blue-600 text-white px-2 py-0.5 rounded-full text-xs">
                                {elem.hitCount}
                              </span>
                            </td>
                            <td className="px-2 py-1 text-gray-300">
                              <div className="flex flex-wrap gap-1">
                                {elem.directions.map(dir => (
                                  <span key={dir} className="bg-gray-700 px-1.5 py-0.5 rounded text-xs">
                                    {dir}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-2 py-1 text-gray-300">
                              {elem.averageDistance.toFixed(2)}ft
                            </td>
                            <td className="px-2 py-1 text-center">
                              <button
                                onClick={() => toggleElementHighlight(elem.dbId)}
                                className={`px-2 py-1 rounded text-xs ${
                                  highlightedElements.has(elem.dbId)
                                    ? 'bg-red-600 text-white'
                                    : 'bg-gray-600 text-gray-300'
                                }`}
                              >
                                {highlightedElements.has(elem.dbId) ? 'Hide' : 'Show'}
                              </button>
                            </td>
                          </tr>
                        ))}
                        
                        {/* Virtual spacer for items after visible range */}
                        {visibleRange.end < results.detectedElements.length && (
                          <tr style={{ height: `${(results.detectedElements.length - visibleRange.end) * 32}px` }}>
                            <td colSpan={6}></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">No structural elements detected (elements with Revit IDs)</p>
                )}
              </div>
            </div>
          )}

          {/* Info Message */}
          {!isModelLoaded && (
            <div className="flex items-center gap-2 p-3 bg-yellow-900/20 text-yellow-200 rounded-md">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">Please load a model first</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}